import { gql } from '@apollo/client';

import type { SessionContext } from '../api/generated/types_web';

/**
 * Minimal query for the More Info screen. Use this instead of the full `getMediaByIDsQuery`,
 * which also pulls tabs, seasons, sponsorships, etc. that this screen never renders.
 *
 * `id` is deliberately not selected, so Apollo stores the result inline under the
 * `medias(<args>)` root field (keyed by its variables, including language/maturity) instead of
 * normalizing it into the shared `MediaMetadata:<id>` entity. That keeps a cache hit from
 * returning text written by another query in a different language.
 *
 * The slot is keyed by field + arguments, not by query name: any other `medias` query with
 * identical variables (string ids, same sessionContext) shares it, last write wins. Don't add
 * such a query that also selects `id`, or this guarantee no longer holds.
 */
export const getMoreInfoMediaByIDsQuery = gql`
  query moreInfoMedias($sessionContext: SessionContext!, $ids: [String!]!) {
    medias(sessionContext: $sessionContext, ids: $ids) {
      title
      description
      genres {
        displayName
      }
      maturityRatings {
        displayName
      }
      metadataImages {
        poster {
          url
        }
      }
      details {
        cast {
          role
          members {
            displayName
          }
        }
      }
    }
  }
`;

// Shared by the page hook and the prefetch so both hit the same cache entry.
export const getMoreInfoQueryVariables = (
  sessionContext: SessionContext,
  mediaId: string
) => ({ sessionContext, ids: [mediaId] });
